import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toEnPath, toItPath } from '@/i18n/slugs';

const SITE_NAME = 'Unity Consulting';
const DEFAULT_OG_IMAGE = '/assets/logo-extended.svg';
const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href, hreflang) => {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const stripTrailingSlash = (url) => url.length > 1 ? url.replace(/\/$/, '') : url;
const canonicalOrigin = () => {
  const configured = process.env.REACT_APP_SITE_URL || 'https://unity-consult.ch';
  try {
    const url = new URL(configured);
    url.protocol = 'https:';
    url.hostname = url.hostname.replace(/^www\./, '');
    url.pathname = '';
    url.search = '';
    url.hash = '';
    return stripTrailingSlash(url.toString());
  } catch (_) {
    return 'https://unity-consult.ch';
  }
};

export const useSeo = ({
  title,
  description,
  image,
  imageAlt,
  type = 'website',
  faq,
  keywords,
  robots = DEFAULT_ROBOTS,
  schemaType = 'WebPage',
  breadcrumbs = [],
}) => {
  const { pathname } = useLocation();
  const lang = pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'it';

  useEffect(() => {
    const origin = canonicalOrigin();
    const noPrefix = lang === 'en' ? pathname.replace(/^\/en(?=\/|$)/, '') || '/' : pathname;
    const itPath = lang === 'en' ? toItPath(noPrefix) : pathname;
    const enSlug = toEnPath(itPath);
    const itUrl = origin + itPath;
    const enUrl = origin + (enSlug === '/' ? '/en' : '/en' + enSlug);
    const canonicalUrl = origin + pathname;
    const safeTitle = title || SITE_NAME;
    const fullTitle = safeTitle.includes(SITE_NAME) ? safeTitle : `${safeTitle} | ${SITE_NAME}`;
    const rawOgImage = image || DEFAULT_OG_IMAGE;
    const ogImage = /^https?:\/\//.test(rawOgImage) ? rawOgImage : `${origin}${rawOgImage.startsWith('/') ? rawOgImage : `/${rawOgImage}`}`;
    const socialImageAlt = imageAlt || safeTitle;

    document.documentElement.lang = lang;
    document.title = fullTitle;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('name', 'googlebot', robots);
    setMeta('name', 'keywords', keywords?.length ? (Array.isArray(keywords) ? keywords.join(', ') : keywords) : '');

    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:alt', socialImageAlt);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', lang === 'en' ? 'en_GB' : 'it_CH');
    setMeta('property', 'og:locale:alternate', lang === 'en' ? 'it_CH' : 'en_GB');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:image:alt', socialImageAlt);

    setLink('canonical', canonicalUrl);
    setLink('alternate', itUrl, 'it-CH');
    setLink('alternate', enUrl, 'en');
    setLink('alternate', itUrl, 'x-default');
    setLink('icon', `${origin}/favicon.ico`);
    setLink('apple-touch-icon', `${origin}/apple-touch-icon.png`);
    setLink('manifest', `${origin}/site.webmanifest`);

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      url: `${origin}/`,
      name: SITE_NAME,
      inLanguage: ['it-CH', 'en'],
    };

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceAgency',
      '@id': `${origin}/#organization`,
      name: SITE_NAME,
      legalName: 'Unity Consulting di Matteo Vairani',
      url: `${origin}/`,
      logo: `${origin}/assets/logo-extended.svg`,
      description: lang === 'en'
        ? 'Independent insurance broker for individuals and businesses in Switzerland. Wealth & Risk Consulting is our approach to insurance, pensions and risk management.'
        : 'Broker assicurativo indipendente per privati e aziende in Svizzera. Wealth & Risk Consulting è il nostro approccio ad assicurazioni, previdenza e gestione dei rischi.',
      slogan: 'Wealth & Risk Consulting',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Antonio Chiesa 12a',
        postalCode: '6830',
        addressLocality: 'Chiasso',
        addressRegion: 'TI',
        addressCountry: 'CH',
      },
      areaServed: { '@type': 'Country', name: 'Switzerland' },
      knowsLanguage: ['Italian', 'English', 'German', 'French'],
      taxID: 'CHE-275.494.791',
      identifier: {
        '@type': 'PropertyValue',
        name: 'Numero di registrazione FINMA',
        value: 'F01574165',
      },
    };

    const pageSchema = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      '@id': `${canonicalUrl}#page`,
      url: canonicalUrl,
      name: fullTitle,
      description,
      inLanguage: lang === 'en' ? 'en' : 'it-CH',
      isPartOf: { '@id': `${origin}/#website` },
      provider: { '@id': `${origin}/#organization` },
      primaryImageOfPage: ogImage ? { '@type': 'ImageObject', url: ogImage } : undefined,
    };

    const breadcrumbSchema = breadcrumbs?.length ? {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        ...(item.href ? { item: origin + (lang === 'en' ? (item.href === '/' ? '/en' : '/en' + toEnPath(item.href)) : item.href) } : {}),
      })),
    } : null;

    const faqSchema = faq?.length ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    } : null;

    setJsonLd('website-jsonld', websiteSchema);
    setJsonLd('organization-jsonld', organizationSchema);
    setJsonLd('page-jsonld', pageSchema);
    setJsonLd('breadcrumb-jsonld', breadcrumbSchema);
    setJsonLd('faq-jsonld', faqSchema);
  }, [title, description, image, imageAlt, type, pathname, lang, faq, keywords, robots, schemaType, breadcrumbs]);
};
